import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PhoneCall, ArrowLeft, LockKey, CheckCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (pw.length < 6) { setErr("Password must be at least 6 characters."); return; }
        if (pw !== confirm) { setErr("Passwords do not match."); return; }
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pw });
            setDone(true);
            toast.success("Password updated");
            setTimeout(() => navigate("/login"), 1800);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="grid min-h-screen place-items-center bg-paper px-6">
            <div className="w-full max-w-md">
                <div className="mb-8 flex">
                    <img src="/inspiro-connect-logo.png" alt="Inspiro Connect" className="h-9 w-auto object-contain" />
                </div>

                {done ? (
                    <div className="rounded-2xl border border-stone-200 bg-white p-8 text-center" data-testid="reset-success">
                        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-100">
                            <CheckCircle size={24} weight="fill" className="text-emerald-600" />
                        </div>
                        <h1 className="mt-4 font-heading text-2xl font-bold">Password updated</h1>
                        <p className="mt-2 text-sm text-stone2">Redirecting you to sign in…</p>
                    </div>
                ) : !token ? (
                    <div className="rounded-2xl border border-stone-200 bg-white p-8 text-center" data-testid="reset-invalid">
                        <h1 className="font-heading text-2xl font-bold">Invalid reset link</h1>
                        <p className="mt-2 text-sm text-stone2">This link is missing its token. Please request a new one.</p>
                        <Link to="/forgot-password" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:underline">
                            Request a new link
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Set a new password</div>
                        <h1 className="mt-3 font-heading text-4xl font-bold leading-tight tracking-tight">Choose a new <span className="text-brand">password</span>.</h1>

                        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-widest text-stone2">New password</label>
                                <div className="relative mt-2">
                                    <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                                    <input
                                        data-testid="reset-password-input"
                                        type="password"
                                        required
                                        value={pw}
                                        onChange={(e) => setPw(e.target.value)}
                                        className="w-full rounded-lg border border-stone-300 bg-white py-3 pl-10 pr-4 text-base outline-none transition-all focus:border-ink focus:ring-2 focus:ring-brand/30"
                                        placeholder="At least 6 characters"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-widest text-stone2">Confirm password</label>
                                <input
                                    data-testid="reset-confirm-input"
                                    type="password"
                                    required
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    className="mt-2 w-full rounded-lg border border-stone-300 bg-white px-4 py-3 text-base outline-none transition-all focus:border-ink focus:ring-2 focus:ring-brand/30"
                                    placeholder="Re-enter password"
                                />
                            </div>
                            {err && <div data-testid="reset-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
                            <button
                                data-testid="reset-submit-button"
                                type="submit"
                                disabled={busy}
                                className="w-full rounded-full bg-ink py-3.5 font-heading text-base font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-stone-800 disabled:opacity-50"
                            >
                                {busy ? "Updating…" : "Update password"}
                            </button>
                        </form>

                        <Link to="/login" data-testid="back-to-login" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-stone2 hover:text-ink">
                            <ArrowLeft size={16} /> Back to sign in
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
